import React, { useEffect, useState } from 'react';

const ResultCelebration = ({ result, message, onClose, onTweet }) => {
  const [allRead, setAllRead] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [confetti, setConfetti] = useState([]);

  // Generate confetti particles
  useEffect(() => {
    const particles = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.5,
      duration: 2 + Math.random() * 1,
    }));
    setConfetti(particles);

    // Play success sound
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const playTone = (freq, duration) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.connect(gain);
      gain.connect(audioContext.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
      osc.start(audioContext.currentTime);
      osc.stop(audioContext.currentTime + duration);
    };
    // Happy success sound: ascending notes
    playTone(523.25, 0.15); // C5
    setTimeout(() => playTone(659.25, 0.15), 150); // E5
    setTimeout(() => playTone(783.99, 0.3), 300); // G5
  }, []);

  // Track scroll to enable close button
  useEffect(() => {
    const handleScroll = (e) => {
      const target = e.target;
      const scrollPercent = (target.scrollTop / (target.scrollHeight - target.clientHeight)) * 100;
      setScrollProgress(scrollPercent);
      if (scrollPercent > 95) {
        setAllRead(true);
      }
    };
    const contentEl = document.getElementById('celebration-content');
    if (contentEl) {
      contentEl.addEventListener('scroll', handleScroll);
      return () => contentEl.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      {/* Confetti */}
      {confetti.map((particle) => (
        <div
          key={particle.id}
          className="fixed w-2 h-2 rounded-full pointer-events-none"
          style={{
            left: `${particle.left}%`,
            top: '-10px',
            background: ['#7c3aed', '#ec4ebc', '#f97316', '#fbbf24', '#34d399'][Math.floor(Math.random() * 5)],
            animation: `fall ${particle.duration}s linear ${particle.delay}s forwards`,
          }}
        />
      ))}

      <style>{`
        @keyframes fall {
          to {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(124, 58, 237, 0.5); }
          50% { box-shadow: 0 0 40px rgba(124, 58, 237, 0.8); }
        }
        @keyframes bounce-in {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>

      {/* Main Card */}
      <div
        className="bg-gradient-to-br from-violet-600 via-violet-700 to-violet-900 rounded-3xl p-8 max-w-md w-full shadow-2xl transform transition-all"
        style={{
          animation: 'bounce-in 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
          boxShadow: '0 0 60px rgba(124, 58, 237, 0.6), 0 20px 60px rgba(0, 0, 0, 0.3)',
        }}
      >
        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-violet-400 bg-opacity-20 rounded-t-3xl overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-yellow-300 via-pink-400 to-green-400 transition-all duration-300"
            style={{ width: `${scrollProgress}%` }}
          />
        </div>

        {/* Header with party emojis */}
        <div className="text-center mb-6 pt-4">
          <div className="text-5xl mb-3 animate-bounce" style={{ animationDelay: '0s' }}>
            🎉 🎊 🎉
          </div>
          <h1 className="text-4xl font-black text-white mb-2">¡PROVA REGISTRADA!</h1>
          <p className="text-violet-100 text-lg">Você provou sua existência 🌟</p>
        </div>

        {/* Scrollable content */}
        <div
          id="celebration-content"
          className="bg-violet-900 bg-opacity-50 border-2 border-violet-300 rounded-2xl p-6 mb-6 max-h-64 overflow-y-auto space-y-6"
        >
          {/* Message Preview */}
          <div>
            <p className="text-violet-200 text-sm font-bold mb-2 flex items-center gap-2">
              💬 Sua mensagem
            </p>
            <div className="bg-violet-800 bg-opacity-60 border-l-4 border-pink-400 p-4 rounded text-white italic">
              "{message}"
            </div>
          </div>

          {/* TX Hash */}
          <div>
            <p className="text-violet-200 text-sm font-bold mb-2 flex items-center gap-2">
              ⛓️ Hash da Transação
            </p>
            <a
              href={`https://polygonscan.com/tx/${result.tx.hash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-violet-800 bg-opacity-60 p-4 rounded text-yellow-300 font-mono text-xs break-all hover:text-yellow-100 transition border border-yellow-500 border-opacity-30"
            >
              {result.tx.hash}
            </a>
            <p className="text-violet-300 text-xs mt-2">🔗 Clique para verificar no Polygonscan</p>
          </div>

          {/* Proof Hash */}
          <div>
            <p className="text-violet-200 text-sm font-bold mb-2 flex items-center gap-2">
              🔐 Hash da Prova
            </p>
            <div className="bg-violet-800 bg-opacity-60 p-4 rounded text-green-300 font-mono text-xs break-all border border-green-500 border-opacity-30">
              {result.proofHash}
            </div>
          </div>

          {/* Scroll hint */}
          <div className="text-center text-violet-300 text-xs italic">
            📜 Role para baixo para desbloquear o botão de fechar
          </div>
        </div>

        {/* Tweet Button (if enabled) */}
        {result.tweeted !== false && (
          <button
            onClick={onTweet}
            className="w-full py-3 bg-gradient-to-r from-blue-400 to-blue-600 text-white font-bold rounded-xl mb-4 hover:from-blue-500 hover:to-blue-700 transition shadow-lg flex items-center justify-center gap-2"
          >
            𝕏 Publicar no X
          </button>
        )}

        {/* Close Button - Conditionally enabled */}
        <button
          onClick={onClose}
          disabled={!allRead}
          className={`w-full py-4 font-bold rounded-xl text-lg transition-all ${
            allRead
              ? 'bg-gradient-to-r from-green-400 to-emerald-500 text-white hover:from-green-500 hover:to-emerald-600 shadow-lg'
              : 'bg-gray-500 text-gray-300 opacity-50 cursor-not-allowed'
          }`}
        >
          {allRead ? '✨ Concluído! Fechar ✨' : '👀 Leia tudo para continuar'}
        </button>

        {/* Footer celebration */}
        <div className="text-center mt-4 text-violet-200 text-sm">
          <p>🚀 Sua existência foi comprovada na blockchain! 🌍</p>
          <p className="text-xs text-violet-300 mt-1">Compartilhe seu momento de glória!</p>
        </div>
      </div>
    </div>
  );
};

export default ResultCelebration;
